import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, HandHeart } from 'lucide-react';
import { PageHeader } from './MassTimes';
import EditableText from '../components/EditableText';
import DocumentDownload from '../components/DocumentDownload';

const SECTIONS = [
  {
    key: 'becoming_catholic_intro',
    number: '1',
    title: 'How do I find out about Catholicism?',
    defaultValue: `One of the best ways is to contact a Catholic parish near you to let them know that you are interested in finding out more about the Catholic Faith. You will be invited to inquire into what "being Catholic" is all about.

Our parish office at All Saints Catholic Parish is also here to help. We warmly welcome your questions, whether you're just curious, exploring your faith after time away, or ready to begin the journey.`,
  },
  {
    key: 'becoming_catholic_what_if',
    number: '2',
    title: 'What if I think that I want to become Catholic?',
    defaultValue: `We believe that God calls individuals to live the Good News Jesus proclaimed and to become his disciples as members of the Catholic Church.

If you want to become Catholic, you will participate in a process called the Rite of Christian Initiation of Adults (sometimes called RCIA or simply Christian initiation). In RCIA, you will learn about the teachings of Jesus as the Catholic Church has handed them on, and put these teachings into practice by living as part of the Catholic community, serving others, and praying.`,
  },
  {
    key: 'becoming_catholic_entail',
    number: '3',
    title: 'What does initiation into the Catholic Church entail?',
    defaultValue: `If you have not been baptized, the RCIA process prepares you to become a Catholic Christian by celebrating what we call the sacraments of Christian initiation: Baptism, Confirmation, and Eucharist.

If you have been baptized in another Christian tradition, the RCIA process prepares you to enter into full communion with the Catholic Church by celebrating the sacraments of Confirmation and Eucharist.

A typical RCIA process takes you from the pre-catechumenate period of inquiry to the Rite of Acceptance. Then you will go through the Catechumenate period of formation to the Rite of Election, followed by a period of purification and enlightenment to receive the Sacraments of Initiation. You will go through a short period of post-baptismal catechesis for spiritual growth.`,
  },
  {
    key: 'becoming_catholic_baptized',
    number: '4',
    title: "I'm a baptized Catholic. Is RCIA for me?",
    defaultValue: `If you have been baptized as an infant, but have not received further formal instruction in the Catholic Faith nor the sacraments of Confirmation or the Eucharist, RCIA is indeed for you.

RCIA welcomes those who are already baptized Catholic but wish to complete their sacraments of initiation and grow more deeply in their faith.`,
  },
  {
    key: 'becoming_catholic_how_long',
    number: '5',
    title: 'How long does it take to become a Catholic?',
    defaultValue: `Becoming Catholic is a life-long commitment. However, the Christian initiation process typically lasts for several months or longer in order to prepare to take this step.

Participants in the initiation process generally meet once or twice each week, including part of the Mass on Sunday, but each parish initiation schedule varies.

The initiation of individuals who are ready to make the commitment to become new members of the Catholic Church typically takes place at the Easter Vigil, or at other times deemed necessary.`,
  },
  {
    key: 'becoming_catholic_change_mind',
    number: '6',
    title: 'What if I change my mind?',
    defaultValue: `While we love being Catholic and hope that others will want to become Catholic also, we do not exert pressure on anyone to do so. Our hope would be that at the very least, you would come to a greater appreciation of Catholic beliefs and practices.

You do not make any commitment or obligation by inquiring about Catholicism or beginning the Christian initiation process. We want you to take the time you need as you think about becoming Catholic and we respect your decision to stop or to say, "not right now" at any time.`,
  },
  {
    key: 'becoming_catholic_else',
    number: '7',
    title: 'What else can I do?',
    defaultValue: `Thank you for your interest in discovering or rediscovering the Catholic Faith. We hope you have found helpful resources here to learn more.

As you continue on your journey, we encourage you to keep an open heart, attend Mass at St. Mary's or St. Joseph's, speak with our pastor, and pray daily.`,
  },
];

const BecomingCatholic = () => {
  return (
    <>
      <PageHeader
        eyebrow="Your Journey"
        title="How Do I Become a Catholic?"
        subtitle="Whether you're just curious, exploring, or ready to begin, we would be honored to walk with you as you discover the Catholic Faith."
      />

      {/* Intro / lead */}
      <section className="max-w-3xl mx-auto px-6 pt-14 pb-6">
        <div className="text-center">
          <p className="text-sm tracking-[0.2em] uppercase text-[#a68a5b] font-medium mb-3">The Path to Catholicism</p>
          <EditableText
            contentKey="becoming_catholic_lead"
            label="Becoming Catholic: Lead Paragraph"
            rows={5}
            defaultValue={`Coming home to the Catholic Church is a beautiful journey — one that unfolds at your own pace, in the company of a caring community. Below are answers to some of the most common questions people ask when they first consider becoming Catholic.`}
            paragraphClass="text-lg text-[#3f3a34] leading-relaxed font-light"
          />
        </div>
      </section>

      {/* Numbered sections */}
      <section className="max-w-3xl mx-auto px-6 pb-8 space-y-10">
        {SECTIONS.map(s => (
          <article key={s.key} className="grid grid-cols-[auto,1fr] gap-5 md:gap-7">
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#3a2359] text-[#e6d4a8] font-display text-2xl md:text-3xl flex items-center justify-center flex-shrink-0 leading-none">
              {s.number}
            </div>
            <div className="pt-1">
              <h2 className="font-display text-2xl md:text-3xl text-[#2a1a45] leading-snug mb-3">{s.title}</h2>
              <EditableText
                contentKey={s.key}
                label={`Section ${s.number}: ${s.title}`}
                rows={10}
                defaultValue={s.defaultValue}
                paragraphClass="text-[15px] md:text-base text-[#3f3a34] leading-[1.85]"
              />
            </div>
          </article>
        ))}
      </section>

      {/* RCIA Form Download */}
      <section className="max-w-3xl mx-auto px-6 pb-2">
        <DocumentDownload
          documentKey="rcia_form"
          title="RCIA Registration Form"
          description="Fill out this form to begin RCIA at All Saints Catholic Parish. Return it via email or drop it off at the parish office."
          fallback=""
        />
      </section>

      {/* Contact / Registration CTA */}
      <section className="max-w-3xl mx-auto px-6 pt-6 pb-12">
        <div className="bg-[#f2ead6]/60 border border-[#e9e0cc] p-8 md:p-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-full bg-white text-[#3a2359] flex items-center justify-center">
              <HandHeart size={18} />
            </div>
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-[#a68a5b]">Take the Next Step</p>
              <h3 className="font-display text-2xl md:text-3xl text-[#2a1a45]">We'd Love to Hear From You</h3>
            </div>
          </div>
          <EditableText
            contentKey="becoming_catholic_cta"
            label="Becoming Catholic: Contact CTA blurb"
            rows={4}
            defaultValue={`If you have any questions or would like to begin your journey with us, please contact our parish office. There is no obligation — we simply want to walk with you at your own pace.`}
            paragraphClass="text-[15px] text-[#3f3a34] leading-relaxed"
          />
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#3a2359] text-white text-sm hover:bg-[#4d2f75]"
              data-testid="becoming-catholic-contact-btn"
            >
              Contact the Parish Office
            </Link>
            <Link
              to="/new-parishioner"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 border border-[#3a2359] text-[#3a2359] text-sm hover:bg-[#3a2359] hover:text-white"
            >
              Register as a Parishioner <ChevronRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Prayer */}
      <section className="bg-[#2a1a45] text-white py-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-xs tracking-[0.25em] uppercase text-[#e6d4a8] mb-4">A Prayer for the Journey</p>
          <EditableText
            contentKey="becoming_catholic_prayer"
            label="Becoming Catholic: Closing Prayer"
            rows={8}
            defaultValue={`Dear God, help me to see your presence and hear your voice in the world around me.

Guide me on my journey and lead me to you.

In the name of the Father, Son and Holy Spirit.

Amen.`}
            paragraphClass="font-display text-2xl md:text-3xl text-[#f2ead6] italic leading-snug font-light"
          />
        </div>
      </section>
    </>
  );
};

export default BecomingCatholic;
